import {
  filterOutTestnetsFromChainIds,
  getChainComparator,
  getChainIdFromChainName,
  getSupportedChainIds,
  getVirtualMachineSpecificFeatureSpecificSupportedChainIds,
} from "@/config/chains/helpers";
import type { VirtualMachineConfig } from "@/config/chains/types";

import type { V3CustomerAddressWithEntitlements } from "@/gen/orval/webplatform-v3/model";
import { V3CustomerAddressEntitlementStatus } from "@/gen/orval/webplatform-v3/model/v3CustomerAddressEntitlementStatus.gen";

import type { AddressPurpose, CustomerAddress, CustomerAddressEntitlement, EntitlementType } from "../types";

/**
 * Transform v3 address to CustomerAddress with chainId on entitlements
 */
export function toCustomerAddress(
  v3Address: V3CustomerAddressWithEntitlements,
  virtualMachineConfig: VirtualMachineConfig,
): CustomerAddress {
  return {
    ...v3Address,
    virtualMachineConfig,
    entitlements: v3Address.entitlements
      .map((e) => {
        const chainId = getChainIdFromChainName(e.chain);
        if (chainId == null) {
          console.warn(`Unknown chain name "${e.chain}" in entitlement ${e.id}, skipping`);
        }
        return { ...e, chainId: chainId ?? 0 };
      })
      .filter((e) => e.chainId !== 0),
  };
}

/** Addresses that can move money. */
export const MINT_AND_INSTANT_LIQUIDITY_TYPES = [
  "instant_liquidity",
  "mint",
] as const satisfies readonly EntitlementType[];

const ACCOUNTS_LISTED_TYPES_MAP = {
  instant_liquidity: true,
  mint: true,
  rewards: true,
  rewards_payout: true,
  // exhaustive type check
} satisfies Record<EntitlementType, boolean>;

export const ALL_ENTITLEMENT_TYPES: readonly EntitlementType[] = (
  Object.keys(ACCOUNTS_LISTED_TYPES_MAP) as EntitlementType[]
).filter((type) => ACCOUNTS_LISTED_TYPES_MAP[type]);

/**
 * Whether an address carries a live entitlement of one of `entitlementTypes`.
 */
export function hasLiveEntitlementOfType(
  address: CustomerAddress,
  entitlementTypes: readonly EntitlementType[],
): boolean {
  return address.entitlements.some(
    (e) => entitlementTypes.includes(e.type) && e.status !== V3CustomerAddressEntitlementStatus.removed,
  );
}

/** The two buckets the screens talk in. */
const PURPOSE_BY_ENTITLEMENT_TYPE: Record<EntitlementType, AddressPurpose> = {
  instant_liquidity: "mint_and_redeem",
  mint: "mint_and_redeem",
  rewards: "rewards",
  rewards_payout: "rewards",
};

/** Chains where instant liquidity is on offer at all. */
export function isInstantSettlementAvailable(chainId: number): boolean {
  return getSupportedChainIds("SWAP_MINT").includes(chainId);
}

/** Every chain this address could hold an entitlement on. */
export function getSupportedChainIdsForAddress(address: CustomerAddress): number[] {
  const slug = address.virtualMachineConfig?.slug;
  return [
    ...new Set([
      ...filterOutTestnetsFromChainIds(
        getVirtualMachineSpecificFeatureSpecificSupportedChainIds({
          feature: "ATLAS_ADDRESSES",
          virtualMachineSlug: slug,
        }),
      ),
      ...filterOutTestnetsFromChainIds(
        getVirtualMachineSpecificFeatureSpecificSupportedChainIds({
          feature: "RECIPIENT_ADDRESSES",
          virtualMachineSlug: slug,
        }),
      ),
    ]),
  ];
}

/**
 * Return network chain ids not added to an address based on the customer type (rewards only or not)
 */
export function getChainsIdsHaveNotAdded(address: CustomerAddress, rewardsOnlyCustomer: boolean): number[] {
  const supportedChainIds = getSupportedChainIdsForAddress(address);
  const takenChainIds = new Set(getChainsIdsHaveAdded(address, rewardsOnlyCustomer));
  return supportedChainIds.filter((chainId) => !takenChainIds.has(chainId)).sort(getChainComparator((id) => id));
}

/**
 * Return chain ids that have been added to an address based on the customer type (rewards only or not)
 *
 * e.g. a rewards-only customer should only care about rewards entitlements, having a mint or IL on a chain will not be
 * considered as they've added the chain
 */
export function getChainsIdsHaveAdded(address: CustomerAddress, rewardsOnlyCustomer: boolean): number[] {
  const addedChainIds = new Set(
    address.entitlements
      // A strategic partner only ever adds rewards, so any reward counts, otherwise any entitlement counts
      .filter((entitlement) => !rewardsOnlyCustomer || entitlement.type === "rewards")
      .map((entitlement) => entitlement.chainId),
  );
  return [...addedChainIds];
}

/** Which purposes an address currently serves, ignoring entitlements that are already removed. */
export function getAddressPurposes(address: CustomerAddress): AddressPurpose[] {
  const purposes = new Set<AddressPurpose>();
  for (const entitlement of address.entitlements) {
    if (entitlement.status === V3CustomerAddressEntitlementStatus.removed) continue;
    purposes.add(PURPOSE_BY_ENTITLEMENT_TYPE[entitlement.type]);
  }
  return [...purposes];
}

/**
 * Get entitlements grouped by chainId
 */
export function getEntitlementsByChain(address: CustomerAddress): Map<number, CustomerAddressEntitlement[]> {
  const map = new Map<number, CustomerAddressEntitlement[]>();
  for (const e of address.entitlements) {
    if (!map.has(e.chainId)) {
      map.set(e.chainId, []);
    }
    map.get(e.chainId)!.push(e);
  }
  return map;
}

export function getChainIds(address: CustomerAddress): number[] {
  return dedupeChainIds(address.entitlements);
}

export function getSortedChainIds(address: CustomerAddress): number[] {
  return getChainIds(address).sort(getChainComparator((id) => id));
}

/** Return chain ids worth showing (no removed chains) */
export function getUnremovedChainIds(address: CustomerAddress): number[] {
  return dedupeChainIds(address.entitlements.filter((e) => e.status !== V3CustomerAddressEntitlementStatus.removed));
}

function dedupeChainIds(entitlements: CustomerAddressEntitlement[]): number[] {
  return [...new Set(entitlements.map((e) => e.chainId))];
}
